"use client";

import { notFound } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { generateComponent, removeComponent } from "@/lib/server_functions";
import { createSupabaseClient, type Page } from "@/lib/supabase";
import { RemoteComponent } from "../remote_component";

export default function SlugPage({
  params,
}: {
  params: Promise<{ slug: string }>;
}) {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState("");
  const [generatingComponent, setGeneratingComponent] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    params.then((resolvedParams) => {
      setSlug(resolvedParams.slug);
    });
  }, [params]);

  useEffect(() => {
    if (!slug) return;

    const supabase = createSupabaseClient();

    // Initial fetch
    const fetchPage = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from("pages")
          .select("*")
          .eq("path", slug)
          .single();

        if (error) {
          if (error.code === "PGRST116") {
            // No rows returned
            notFound();
          }
          throw error;
        }
        console.log(data);
        setPage(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchPage();

    // Set up real-time subscription
    const channel = supabase
      .channel(`pages:${slug}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "pages",
          filter: `path=eq.${slug}`,
        },
        (payload) => {
          console.log("Real-time update:", payload);

          if (payload.eventType === "DELETE") {
            setPage(null);
          } else if (
            payload.eventType === "INSERT" ||
            payload.eventType === "UPDATE"
          ) {
            setPage(payload.new as Page);
          }
        }
      )
      .subscribe();

    // Cleanup subscription
    return () => {
      supabase.removeChannel(channel);
    };
  }, [slug]);

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatMessage.trim() || generatingComponent || !page) return;

    setGeneratingComponent(true);
    try {
      await generateComponent(page.id, chatMessage);
      setChatMessage("");
      // Component will be automatically added to the page via real-time subscription
    } catch (error) {
      console.error("Error generating component:", error);
      setError("Failed to generate component");
    } finally {
      setGeneratingComponent(false);
    }
  };

  const handleRemoveComponent = async (componentIndex: number) => {
    if (!page) return;

    try {
      await removeComponent(page.id, componentIndex);
      // Page will be automatically updated via real-time subscription
    } catch (error) {
      console.error("Error removing component:", error);
      setError("Failed to remove component");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Page not found</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div
        className="container mx-auto p-8"
        style={
          page.settings.variableOverrides
            ? page.settings.variableOverrides
            : undefined
        }
      >
        {/* Header with title and edit icon */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold">{page.title}</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            className="flex items-center gap-2"
          >
            ✏️ Edit
          </Button>
        </div>

        {/* All components from page settings (including newly generated ones) */}
        {page.settings.components?.map((component, index) => (
          <div key={`${index}-${component.id}`} className="mb-4">
            <RemoteComponent
              id={component.id}
              onRemove={() => handleRemoveComponent(index)}
              {...component.props}
            />
          </div>
        ))}
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Sidebar */}
          <div className="relative ml-auto w-96 bg-gradient-to-br from-white via-slate-50 to-blue-50 shadow-2xl border-l border-slate-200">
            <div className="flex h-full flex-col">
              {/* Sidebar header */}
              <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Edit Interface</h2>
                    <p className="text-blue-100 text-sm mt-1">Customize your page</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSidebarOpen(false)}
                    className="text-white hover:bg-white/20 rounded-full h-8 w-8 p-0"
                  >
                    ✕
                  </Button>
                </div>
              </div>

              {/* Chat interface */}
              <div className="flex-1 p-6 space-y-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-blue-500 rounded-full"></div>
                    <h3 className="font-semibold text-slate-800">AI Assistant</h3>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Tell me what you'd like to do! I can help you add components, 
                    change styling, modify the page title, or remove elements.
                  </p>
                </div>

                <div className="space-y-4">
                  <form onSubmit={handleChatSubmit} className="space-y-3">
                    <Input
                      type="text"
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      placeholder="e.g., 'Add a chart component' or 'Change to blue theme'"
                      disabled={generatingComponent}
                      className="w-full bg-white/90 backdrop-blur-sm border-slate-300 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg px-4 py-3"
                    />
                    <Button
                      type="submit"
                      disabled={generatingComponent || !chatMessage.trim()}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      {generatingComponent ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Processing...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>✨</span>
                          Send Message
                        </div>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
